import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Lock, X } from "lucide-react";

export const CONSENT_KEY = "cookie_consent_preferences";
export const OPEN_EVENT = "open-cookie-preferences";

export const readConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const openCookiePreferences = () => window.dispatchEvent(new Event(OPEN_EVENT));

const writeConsent = (prefs) => {
  const value = { essential: true, analytics: !!prefs.analytics, marketing: !!prefs.marketing, unclassified: !!prefs.unclassified, savedAt: new Date().toISOString() };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(value));
  } catch { /* storage unavailable */ }
  // Apply immediately: the index.html bootstrap exposes this to (de)activate PostHog without a reload.
  try {
    if (typeof window.__applyCookieConsent === "function") window.__applyCookieConsent(value);
  } catch { /* ignore */ }
  return value;
};

const Toggle = ({ checked, disabled, onChange, testid, label }) => (
  <button
    type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled}
    onClick={() => !disabled && onChange(!checked)} data-testid={testid}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${checked ? "bg-sky-500" : "bg-slate-700"} ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
  </button>
);

const Row = ({ title, desc, checked, disabled, onChange, locked, testid }) => (
  <div className="flex items-start justify-between gap-4 py-3.5 border-b border-slate-800 last:border-0">
    <div className="min-w-0">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
        {title}
        {locked && <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-emerald-400"><Lock className="w-3 h-3" /> Always on</span>}
      </div>
      <p className="text-xs text-slate-400 mt-1 leading-relaxed">{desc}</p>
    </div>
    <Toggle checked={checked} disabled={disabled} onChange={onChange} testid={testid} label={title} />
  </div>
);

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showCenter, setShowCenter] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [unclassified, setUnclassified] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const saved = readConsent();
    if (!saved) setShowBanner(true);
    else { setAnalytics(!!saved.analytics); setMarketing(!!saved.marketing); setUnclassified(!!saved.unclassified); }
    const open = () => {
      const s = readConsent();
      if (s) { setAnalytics(!!s.analytics); setMarketing(!!s.marketing); setUnclassified(!!s.unclassified); }
      setShowCenter(true);
    };
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);

  const persist = useCallback((prefs) => {
    writeConsent(prefs);
    setShowBanner(false);
    setShowCenter(false);
  }, []);

  const acceptAll = () => persist({ analytics: true, marketing: true, unclassified: true });
  const rejectNonEssential = () => persist({ analytics: false, marketing: false, unclassified: false });
  const saveCenter = () => persist({ analytics, marketing, unclassified });

  if (!showBanner && !showCenter) return null;

  return (
    <>
      {showBanner && !showCenter && (
        <div data-testid="cookie-banner" role="region" aria-label="Cookie consent" className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4">
          <div className="mx-auto max-w-4xl rounded-2xl bg-slate-900/95 backdrop-blur ring-1 ring-slate-700 shadow-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <Cookie className="w-6 h-6 text-amber-400 shrink-0 hidden sm:block" />
            <p className="text-sm text-slate-200 flex-1 leading-relaxed">
              We use essential cookies to run the Sanctuary. With your consent we also use analytics and marketing cookies; none load until you choose. Manage your preferences or learn more in our{" "}
              <Link to="/cookies" className="text-sky-300 hover:text-sky-200 underline underline-offset-2">Cookie Policy</Link>.
            </p>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <button onClick={() => setShowCenter(true)} data-testid="cookie-banner-manage" className="text-xs font-medium text-slate-300 hover:text-sky-300 px-3 py-2">Manage Preferences</button>
              <button onClick={rejectNonEssential} data-testid="cookie-banner-reject" className="text-xs font-semibold rounded-full px-4 py-2 ring-1 ring-slate-600 text-slate-200 hover:bg-slate-800">Reject Non-Essential</button>
              <button onClick={acceptAll} data-testid="cookie-banner-accept" className="text-xs font-semibold rounded-full px-4 py-2 bg-sky-500 text-slate-950 hover:bg-sky-400">Accept All</button>
            </div>
          </div>
        </div>
      )}

      {showCenter && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="cookie-center">
          <div className="absolute inset-0 bg-black/70" onClick={() => { setShowCenter(false); if (!readConsent()) setShowBanner(true); }} />
          <div className="relative w-full sm:max-w-lg bg-[#0A0F16] ring-1 ring-slate-700 rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3 mb-1">
              <h2 className="text-lg font-display font-bold text-slate-100 flex items-center gap-2"><Cookie className="w-5 h-5 text-amber-400" /> Cookie Preferences</h2>
              <button onClick={() => { setShowCenter(false); if (!readConsent()) setShowBanner(true); }} data-testid="cookie-center-close" data-dialog-close aria-label="Close cookie preferences" className="text-slate-400 hover:text-slate-200"><X className="w-5 h-5" aria-hidden="true" /></button>
            </div>
            <p className="text-xs text-slate-400 mb-3">Choose which cookies we may use. See our <Link to="/cookies" className="text-sky-400 hover:text-sky-300">Cookie Policy</Link> for details.</p>
            <div>
              <Row title="Essential cookies" locked disabled checked onChange={() => {}} testid="cookie-toggle-essential"
                desc="Strictly necessary to provide the Sanctuary and keep it secure, such as access to secure areas. These cannot be disabled." />
              <Row title="Analytics cookies (PostHog)" checked={analytics} onChange={setAnalytics} testid="cookie-toggle-analytics"
                desc="Help us understand how the Sanctuary is used so we can improve it. Off by default." />
              <Row title="Marketing cookies" checked={marketing} onChange={setMarketing} testid="cookie-toggle-marketing"
                desc="Would measure campaigns and personalise promotions. We don't run any marketing trackers today; this keeps your choice ready. Off by default." />
              <Row title="Unclassified cookies" checked={unclassified} onChange={setUnclassified} testid="cookie-toggle-unclassified"
                desc="Cookies we are still in the process of classifying with their providers. Off by default." />
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 mt-5">
              <button onClick={rejectNonEssential} data-testid="cookie-center-reject" className="text-xs font-semibold rounded-full px-4 py-2 ring-1 ring-slate-600 text-slate-200 hover:bg-slate-800">Reject Non-Essential</button>
              <button onClick={acceptAll} data-testid="cookie-center-accept-all" className="text-xs font-semibold rounded-full px-4 py-2 ring-1 ring-slate-600 text-slate-200 hover:bg-slate-800">Accept All</button>
              <button onClick={saveCenter} data-testid="cookie-center-save" className="text-xs font-semibold rounded-full px-4 py-2 bg-sky-500 text-slate-950 hover:bg-sky-400">Save Preferences</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
